import React, { useCallback, useRef, useState } from 'react';
import {
  TouchableOpacity,
  ActionSheetIOS,
  Platform,
  StyleSheet,
  Text,
  View,
  Pressable,
} from 'react-native';
import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { useSheetTabBarHold } from '../../hooks/useSheetTabBarHold';
import { useDesktopSheetStyle } from '../../hooks/useDesktopSheetStyle';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useWebHover } from '../../hooks/useWebHover';
import { SheetBackdrop } from './SheetBackdrop';
import { Popover } from './Popover';

interface ContentActionMenuProps {
  // Optional since Aug 2026: a moderator deleting someone else's content
  // gets a delete-only menu (editing stays author-only, mirroring rules).
  onEdit?: () => void;
  onDelete: () => void;
  size?: number;
  /**
   * Anchor override for the desktop-shell popover. Every call site today
   * omits this — the "More options" button this component renders is
   * anchor enough — so this only matters if some future caller wants the
   * popover to hang off something else instead.
   */
  anchorRef?: React.RefObject<unknown>;
}

/** A row inside the shared menu content: a rounded hover fill on web (the
 *  desktop popover), inert everywhere else (useWebHover is a no-op off
 *  web, and touch has no hover to trigger). Same row used for the phone
 *  bottom sheet, where the fill just never shows. */
function ActionMenuRow({
  testID,
  onPress,
  children,
}: {
  testID: string;
  onPress: () => void;
  children: React.ReactNode;
}) {
  const { hovered, hoverProps } = useWebHover();
  const { colors } = useTheme();
  return (
    <Pressable
      testID={testID}
      style={[
        styles.menuItem,
        { borderRadius: BORDER_RADIUS.sm },
        hovered && { backgroundColor: colors.hoverSurface },
      ]}
      onPress={onPress}
      {...hoverProps}>
      {children}
    </Pressable>
  );
}

export function ContentActionMenu({
  onEdit,
  onDelete,
  size = 16,
  anchorRef,
}: ContentActionMenuProps) {
  const { colors } = useTheme();
  const { hold, release } = useSheetTabBarHold();
  const modalRef = useRef<BottomSheetModal>(null);
  const sheetStyle = useDesktopSheetStyle();
  const isDesktopShell = useDesktopShell();
  const triggerRef = useRef<React.ElementRef<typeof TouchableOpacity>>(null);
  const effectiveAnchorRef = anchorRef ?? triggerRef;
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handlePress = useCallback(() => {
    if (Platform.OS === 'ios') {
      const options = onEdit ? ['Cancel', 'Edit', 'Delete'] : ['Cancel', 'Delete'];
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options,
          destructiveButtonIndex: options.length - 1,
          cancelButtonIndex: 0,
        },
        (buttonIndex) => {
          if (onEdit && buttonIndex === 1) onEdit();
          if (buttonIndex === options.length - 1 && buttonIndex !== 0) onDelete();
        }
      );
    } else if (isDesktopShell) {
      setPopoverOpen((v) => !v);
    } else {
      hold();
      modalRef.current?.present();
    }
  }, [onEdit, onDelete, hold, isDesktopShell]);

  const dismiss = useCallback(() => {
    modalRef.current?.dismiss();
    release();
    setPopoverOpen(false);
  }, [release]);

  const handleEdit = useCallback(() => {
    dismiss();
    onEdit?.();
  }, [dismiss, onEdit]);

  const handleDelete = useCallback(() => {
    dismiss();
    onDelete();
  }, [dismiss, onDelete]);

  const menuItems = (
    <>
      {onEdit && (
        <ActionMenuRow testID="content-action-edit" onPress={handleEdit}>
          <Pencil size={20} color={colors.text} />
          <Text style={[styles.menuItemText, { color: colors.text }]}>Edit</Text>
        </ActionMenuRow>
      )}
      <ActionMenuRow testID="content-action-delete" onPress={handleDelete}>
        <Trash2 size={20} color={colors.danger} />
        <Text style={[styles.menuItemText, { color: colors.danger }]}>Delete</Text>
      </ActionMenuRow>
    </>
  );

  return (
    <>
      <TouchableOpacity
        ref={triggerRef}
        onPress={handlePress}
        activeOpacity={0.6}
        style={styles.button}
        accessibilityRole="button"
        accessibilityLabel="More options">
        <MoreHorizontal size={size} color={colors.textTertiary} style={styles.icon} />
      </TouchableOpacity>

      {isDesktopShell && Platform.OS !== 'ios' && (
        <Popover
          visible={popoverOpen}
          anchorRef={effectiveAnchorRef}
          onClose={() => setPopoverOpen(false)}
          placement="bottom-end"
          testID="content-action-popover">
          <View style={styles.popoverContent}>{menuItems}</View>
        </Popover>
      )}

      {!isDesktopShell && Platform.OS !== 'ios' && (
        <BottomSheetModal
          ref={modalRef}
          snapPoints={[150]}
          enableDynamicSizing={false}
          accessible={false}
          enablePanDownToClose
          backdropComponent={SheetBackdrop}
          backgroundStyle={{ backgroundColor: colors.background }}
          handleIndicatorStyle={{ backgroundColor: colors.textTertiary }}
          onDismiss={release}
          style={sheetStyle}>
          <BottomSheetView style={styles.content}>{menuItems}</BottomSheetView>
        </BottomSheetModal>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  button: {
    padding: 8,
    cursor: 'pointer' as any,
  },
  icon: {
    pointerEvents: 'none' as any,
  },
  content: {
    paddingHorizontal: SPACING.md,
    paddingBottom: SPACING.lg,
  },
  popoverContent: {
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xs,
    minWidth: 160,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.sm,
    gap: SPACING.md,
    cursor: 'pointer' as any,
  },
  menuItemText: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
});
